/**
 * Classify Supabase/PostgREST errors for root-cause diagnostics.
 * Used for developer-facing logs, not user-facing messages.
 */

export type ErrorClass =
  | "malformed_query"
  | "invalid_filter"
  | "rls_denial"
  | "missing_relation"
  | "missing_column"
  | "stale_view_schema"
  | "null_field_mismatch"
  | "bad_select_join"
  | "network_error"
  | "timeout"
  | "unknown";

export interface ClassifiedError {
  class: ErrorClass;
  hint: string;
  code?: string;
  message?: string;
}

/** Check for relation/column missing (schema cache, PGRST205, etc.) */
function isMissingRelationOrColumn(err: { code?: string; message?: string }): boolean {
  const msg = String(err?.message ?? "");
  return (
    err?.code === "PGRST205" ||
    /schema cache/i.test(msg) ||
    /could not find the table/i.test(msg) ||
    /relation .* does not exist/i.test(msg) ||
    /column .* does not exist/i.test(msg)
  );
}

/** Check for RLS-related hints (permission denied, policy violation) */
function suggestsRlsDenial(err: { code?: string; message?: string }): boolean {
  const msg = String(err?.message ?? "").toLowerCase();
  return (
    err?.code === "42501" ||
    err?.code === "PGRST301" ||
    /permission denied/i.test(msg) ||
    /policy/i.test(msg) ||
    /row-level security/i.test(msg) ||
    /violates row-level security/i.test(msg)
  );
}

/** Check for invalid filter/syntax */
function suggestsInvalidFilter(err: { code?: string; message?: string }): boolean {
  const msg = String(err?.message ?? "").toLowerCase();
  return (
    err?.code === "PGRST100" ||
    err?.code === "22P02" ||
    /invalid.*filter/i.test(msg) ||
    /syntax.*error/i.test(msg) ||
    /invalid input/i.test(msg)
  );
}

/** Check for null/required field violations */
function suggestsNullMismatch(err: { code?: string; message?: string }): boolean {
  const msg = String(err?.message ?? "").toLowerCase();
  return (
    err?.code === "23502" ||
    /null value in column/i.test(msg) ||
    /violates not-null/i.test(msg)
  );
}

/** Check for network/timeout */
function suggestsNetworkOrTimeout(err: { code?: string; message?: string } & { name?: string }): boolean {
  const msg = String(err?.message ?? "").toLowerCase();
  const name = String((err as { name?: string })?.name ?? "");
  return (
    name === "AbortError" ||
    name === "TimeoutError" ||
    /network/i.test(msg) ||
    /timeout/i.test(msg) ||
    /fetch/i.test(msg) ||
    /failed to fetch/i.test(msg)
  );
}

/** Check for 400-style API errors */
function is400Style(err: { code?: string }): boolean {
  return err?.code === "PGRST116" || err?.code === "PGRST100" || err?.code === "400";
}

export function classifySupabaseError(
  error: unknown,
  context?: { flow?: string; operation?: string; entity?: string }
): ClassifiedError {
  const err = error && typeof error === "object" ? (error as { code?: string; message?: string; name?: string }) : null;
  const code = err?.code;
  const message = String(err?.message ?? "");

  if (suggestsNetworkOrTimeout(err ?? {})) {
    return {
      class: err?.name === "AbortError" ? "timeout" : "network_error",
      hint: "Network or timeout error. Check connection and retry.",
      code,
      message: message || undefined,
    };
  }

  if (isMissingRelationOrColumn(err ?? {})) {
    return {
      class: /column/i.test(String(err?.message ?? "")) ? "missing_column" : "missing_relation",
      hint: "Table or column does not exist. Schema cache may be stale; verify migrations.",
      code,
      message: message || undefined,
    };
  }

  if (suggestsRlsDenial(err ?? {})) {
    return {
      class: "rls_denial",
      hint: "RLS likely blocking: mutation may succeed but read-back fails, or vice versa.",
      code,
      message: message || undefined,
    };
  }

  if (suggestsInvalidFilter(err ?? {})) {
    return {
      class: "invalid_filter",
      hint: "Invalid filter/sort or query syntax. Check query params and select clause.",
      code,
      message: message || undefined,
    };
  }

  if (suggestsNullMismatch(err ?? {})) {
    return {
      class: "null_field_mismatch",
      hint: "Required field is null. Check insert/update payload.",
      code,
      message: message || undefined,
    };
  }

  if (is400Style(err ?? {})) {
    return {
      class: "malformed_query",
      hint: "Supabase returned 400. Likely malformed query params or select/join.",
      code,
      message: message || undefined,
    };
  }

  return {
    class: "unknown",
    hint: "Unclassified error. Check code and message for details.",
    code,
    message: message || undefined,
  };
}
